import { useState, useCallback } from "react";

const useChatHistory = () => {
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [refreshingHistory, setRefreshingHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [oldestChatDate, setOldestChatDate] = useState(null);

  //const API_BASE_URL = "http://localhost:8000/api/v1";
  const API_BASE_URL = "https://wa-04-productdatadev.azurewebsites.net/api/v1";

  const apiCall = useCallback(async (endpoint, options = {}) => {
    const token = localStorage.getItem("msal_access_token");
    const defaultOptions = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    if (token) {
      defaultOptions.headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...defaultOptions,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response;
  }, []);

  const generateSessionTitle = useCallback((lastMessage) => {
    if (!lastMessage) return null;
    if (
      lastMessage.toLowerCase().includes("assistant") ||
      lastMessage.toLowerCase().includes("help")
    ) {
      return null;
    }
    const title = lastMessage
      .replace(/[^\w\s-]/g, "")
      .trim()
      .substring(0, 50);
    return title || null;
  }, []);

  const loadChatHistory = useCallback(
    async (showRefresh = false) => {
      try {
        if (showRefresh) {
          setRefreshingHistory(true);
        } else {
          setLoadingHistory(true);
        }

        const response = await apiCall("/chat/sessions?limit=10");
        const data = await response.json();

        const formattedSessions = data.sessions.map((session) => ({
          id: session.session_id,
          name:
            session.title ||
            generateSessionTitle(session.last_message_content) ||
            "Untitled Chat",
          lastMessage: session.last_message_content || "AI conversation...",
          timestamp: new Date(session.updated_at || session.created_at),
          messageCount: session.message_count || 0,
          createdAt: new Date(session.created_at),
          updatedAt: new Date(session.updated_at || session.created_at),
        }));

        formattedSessions.sort((a, b) => b.updatedAt - a.updatedAt);
        setChatHistory(formattedSessions);
        setHasMoreChats(data.has_more || false);
        setOldestChatDate(data.oldest_date || null);
      } catch (error) {
        console.error("Failed to load chat history:", error);
      } finally {
        setLoadingHistory(false);
        setRefreshingHistory(false);
      }
    },
    [apiCall, generateSessionTitle]
  );

  const loadMoreChats = useCallback(async () => {
    if (!hasMoreChats || !oldestChatDate) {
      return { success: false, hasMore: false };
    }

    try {
      const response = await apiCall(
        `/chat/sessions?limit=10&before_date=${oldestChatDate}`
      );
      const data = await response.json();

      const newSessions = data.sessions.map((session) => ({
        id: session.session_id,
        name:
          session.title ||
          generateSessionTitle(session.last_message_content) ||
          "Untitled Chat",
        lastMessage: session.last_message_content || "AI conversation...",
        timestamp: new Date(session.updated_at || session.created_at),
        messageCount: session.message_count || 0,
        createdAt: new Date(session.created_at),
        updatedAt: new Date(session.updated_at || session.created_at),
      }));

      setChatHistory((prev) => [...prev, ...newSessions]);
      setHasMoreChats(data.has_more || false);
      setOldestChatDate(data.oldest_date || null);

      return { success: true, hasMore: data.has_more || false };
    } catch (error) {
      console.error("Failed to load more chats:", error);
      return { success: false, hasMore: false };
    }
  }, [apiCall, generateSessionTitle, hasMoreChats, oldestChatDate]);

  const loadSpecificChatHistory = useCallback(
    async (chatId) => {
      try {
        const response = await apiCall(`/chat/history/${chatId}`);
        const data = await response.json();

        if (data.history && data.history.length > 0) {
          const loadedMessages = data.history.map((msg, index) => {
            const message = {
              id: msg.message_id || Date.now() + index,
              type: msg.role === "user" ? "user" : "bot",
              content: msg.content,
              timestamp: new Date(msg.created_at),
              sessionId: chatId,
              metadata: msg.metadata || null,
              feedback: msg.feedback || null,
            };

            if (msg.metadata) {
              // Handle multiple chart configs
              if (msg.metadata.chart_configs) {
                message.chartConfigs = msg.metadata.chart_configs;
                message.chartConfig = msg.metadata.chart_configs[0]; // First for compatibility
              }
              // Handle single chart config (backward compatibility)
              else if (msg.metadata.chart_config) {
                message.chartConfig = msg.metadata.chart_config;
                message.chartConfigs = [msg.metadata.chart_config];
              }

              if (msg.metadata.data) message.queryData = msg.metadata.data;
              if (msg.metadata.response_type)
                message.responseType = msg.metadata.response_type;
              if (msg.metadata.sql_query)
                message.sqlQuery = msg.metadata.sql_query;
              if (msg.metadata.sql_queries)
                message.sqlQueries = msg.metadata.sql_queries;
              if (msg.metadata.record_count !== undefined)
                message.recordCount = msg.metadata.record_count;
              if (msg.metadata.intent) message.intent = msg.metadata.intent;
            }

            return message;
          });

          return loadedMessages;
        } else {
          return [];
        }
      } catch (error) {
        console.error("Failed to load specific chat:", error);
        throw error;
      }
    },
    [apiCall]
  );

  const deleteChatSession = useCallback(
    async (chatId, event, showConfirm) => {
      event.stopPropagation();
      const confirmed = await showConfirm({
        title: "Delete chat session",
        message:
          "Are you sure you want to delete this chat session? This action cannot be undone.",
        confirmText: "Delete",
        cancelText: "Keep",
        variant: "danger",
      });

      if (!confirmed) {
        return;
      }
      try {
        await apiCall(`/chat/sessions/${chatId}`, {
          method: "DELETE",
        });
        setChatHistory((prev) => prev.filter((chat) => chat.id !== chatId));
      } catch (error) {
        console.error("Error deleting chat:", error);
      }
    },
    [apiCall]
  );

  const filteredHistory = chatHistory.filter(
    (chat) =>
      chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );
 
  return {
    chatHistory,
    loadingHistory,
    refreshingHistory,
    searchQuery,
    filteredHistory,
    hasMoreChats,
    oldestChatDate,
    setSearchQuery,
    loadChatHistory,
    loadMoreChats,
    loadSpecificChatHistory,
    deleteChatSession,
  };
};

export default useChatHistory;
